import { useEffect, useRef } from 'react'

import { Button, Grid, Line, Picker, Row } from 'components'
import {
  DraggableDialog,
  resetModalState
} from 'components/DraggableDialog/DraggableDialog'
import moment from 'moment'
import Draggable from 'react-draggable'
import { useForm } from 'react-hook-form'
import { SPACING } from 'utils'
import { yup as Yup, yupResolver } from 'utils/yup'

import {
  DialogActionsStyled,
  DialogContentStyled,
  DialogTitleStyled,
  FormDialog
} from './BaseAnalysisModal.style'

interface IBaseAnalysisModal {
  showModal: boolean
  toggleModal: () => void
  handleSubmitCompetence: any
}

//const schema = YUP.dateRange('competenciaInicio', 'competenciaFim').required

const schema = Yup.object().shape({
  competenciaFim: Yup.string()
    .date()
    .daterange({
      compare: 'competenciaInicio',
      message: 'Data não pode ser menor que a data de início!',
      options: {
        compareEqual: true
      },
      period: 'end'
    })
    .nullable(),
  competenciaInicio: Yup.string()
    .date()
    .daterange({
      compare: 'competenciaFim',
      message: 'Data não pode ser maior que a data de fim!',
      options: {
        compareEqual: true
      },
      period: 'begin'
    })
    .nullable()
})

const BaseAnalysisModal: React.FC<IBaseAnalysisModal> = ({
  showModal,
  toggleModal,
  handleSubmitCompetence
}) => {
  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    trigger,
    formState: { errors }
  } = useForm({ resolver: yupResolver(schema) })
  const competenciaInicio = watch('competenciaInicio')
  const competenciaFim = watch('competenciaFim')

  const DATE_NOW = moment()
    .set({ date: 1, hour: 0, millisecond: 0, minute: 0, second: 0 })
    .toISOString()
  const COMPETENCIA_ATUAL = moment(DATE_NOW).subtract(1, 'month').toISOString()

  useEffect(() => {
    setValue('competenciaFim', DATE_NOW)
    setValue('competenciaInicio', COMPETENCIA_ATUAL)
  }, [showModal])

  useEffect(() => {
    const subs = watch(() => trigger())
    return () => subs.unsubscribe()
  }, [watch])

  const draggableReference = useRef<Draggable>(null)

  return (
    <DraggableDialog
      reference={draggableReference}
      showModal={showModal}
      onClose={toggleModal}
      width={'sm'}
    >
      <FormDialog
        onSubmit={handleSubmit(() => {
          handleSubmitCompetence({
            competenciaFim: moment(competenciaFim).format('yyyyMM').toString(),
            competenciaInicio: moment(competenciaInicio)
              .format('yyyyMM')
              .toString()
          })
          reset()
          resetModalState(draggableReference)
          toggleModal()
        })}
      >
        <Line />
        <DialogTitleStyled>Base Análise</DialogTitleStyled>
        <DialogContentStyled>
          <Grid
            gridCols="1fr 1fr"
            gridGap={`0 ${SPACING.spacing30}`}
            margin={`${SPACING.spacing20} 0 ${SPACING.spacing20} 0`}
          >
            <Picker
              defaultValue={competenciaInicio}
              label="Competência Inicial"
              views={['year', 'month']}
              placeholder="mm/aaaa"
              formValue="competenciaInicio"
              maxDate={moment(competenciaFim)}
              type="monthAndYear"
              control={control}
              errors={errors}
            />
            <Picker
              defaultValue={competenciaFim}
              label="Competência Final"
              views={['year', 'month']}
              placeholder="mm/aaaa"
              formValue="competenciaFim"
              minDate={moment(competenciaInicio)}
              type="monthAndYear"
              control={control}
              errors={errors}
            />
          </Grid>
        </DialogContentStyled>

        <DialogActionsStyled>
          <Button
            text="Voltar"
            variantButton="outlined"
            onClick={() => {
              toggleModal()
              resetModalState(draggableReference)
            }}
            marginBottom={SPACING.spacing20}
          />
          <Row justifyContent="flex-end">
            <Button
              text="Planilha Base Análise"
              type="submit"
              marginLeft={SPACING.spacing20}
              marginRight={SPACING.spacing20}
              marginBottom={SPACING.spacing20}
            />
          </Row>
        </DialogActionsStyled>
      </FormDialog>
    </DraggableDialog>
  )
}

export default BaseAnalysisModal
